<template>
  <TaskDashboardSkeleton v-if="loading" with-header />
  <template v-else-if="localDashboard">
    <div class="shrink-0">
      <DashboardHeader :dashboard="localDashboard">
        <template #cta>
          <!-- The controls stretch to one row height (no margin on the search) and wrap below sm. -->
          <div class="flex flex-wrap gap-2">
            <BaseSimpleSearchInput
              v-model="search"
              size="small"
              :maxlength="100"
              :placeholder="$t('global.tasks_search_placeholder')"
              group-class="flex basis-full sm:min-w-64 sm:basis-64"
            />
            <WalkmanButton
              v-model="view"
              :options="viewOptions"
              track-by="id"
              attribute-label="label"
              compact-labels
            />
            <BaseButton
              v-if="$can('manage_dossier')"
              class="h-9"
              icon="plus"
              :title="$t('button.add_new_f', { var: $t('attributes.task') })"
              @click="getNewTask"
            />
            <DashboardPanelControl
              v-model:dashboard="localDashboard"
              v-model:require-save-dashboard="requireSaveDashboard"
              :need-save-locked="loading"
              :count-filter="validFilter.length ?? 0"
              @dashboard-changed="dashboardChanged"
            >
              <template #display-options-content="{ close }">
                <DisplayOptionsTaskContent
                  :settings="settings"
                  :columns="columns"
                  @update:settings="updateSettings"
                  @close-popover="() => closePopover(close)"
                />
              </template>
              <template #filter-content="{ close }">
                <FilterLinesComponent
                  v-model:filters="localDashboard.filters"
                  v-model:require-save-dashboard="requireSaveDashboard"
                  :filters-available="availableFilter"
                  @close-popover="() => closePopover(close)"
                />
              </template>
            </DashboardPanelControl>
            <RouterLink
              v-if="$can('see_activities')"
              data-test="task-activity-log"
              :to="taskActivityLogRoute"
            >
              <BaseButton
                class="h-9"
                :title="$t('navigation.activity_log_title_main')"
                icon="clock-rotate-left"
                secondary-color
              />
            </RouterLink>
          </div>
        </template>
      </DashboardHeader>
    </div>
    <div class="flex shrink-0 flex-col gap-2 pb-2">
      <TaskActiveSettingsChips
        :settings="settings"
        :label-of="labelOf"
        :hides-done-tasks="hidesDoneTasks"
        @show-done="showDoneTasks"
      />
      <TaskQuickCategoryFilters
        v-model:selected-id="selectedCategoryId"
        :categories="task_categories ?? []"
        :counts="categoryCounts"
        :totals="categoryTotals"
      />
      <TaskBulkActionBar
        v-if="selection.count > 0"
        :count="selection.count"
        :max="maxBulkDelete"
        :busy="isBulkDeleting"
        @delete="handleBulkDelete"
        @clear="selection.clear()"
      />
    </div>
    <div class="h-full overflow-y-scroll">
      <TaskDashboardSkeleton
        v-if="isFetchingTasks"
        :view="settings.view"
        :column-count="boardColumnCount"
      />
      <p
        v-else-if="hasNoTasks"
        data-test="no-task"
        class="py-8 text-center text-sm text-gray-500 dark:text-gray-400"
      >
        {{ $t('global.no_task_assign') }}
      </p>
      <component
        :is="viewComponent"
        v-else
        :groups="groups"
        :settings="settings"
        :show-group-headers="settings.group.column !== null"
        :status-click-cycle="statusClickCycle"
        :status-counts="statusCounts"
        :selection="$can('manage_dossier') ? selection : null"
        @toggle-collapse="toggleCollapse"
        @load-more="loadMore"
        @task-status-updated="handleTaskStatusUpdated"
        @refresh-list="fetchTasks"
        @task-deleted="handleTaskDeleted"
        @sort-by-column="sortByColumn"
      />
    </div>
    <ConfirmModal ref="bulkDeleteConfirm" />
    <TaskDossierModal
      v-model:open="showEditTaskModal"
      v-model:edition-mode="modalEditionMode"
      :refresh-task-dossier="true"
      @refresh-list="fetchTasks"
    />
  </template>
</template>

<script>
import { markRaw } from 'vue'
import moment from 'moment'
import { mapActions, mapGetters } from 'vuex'
import DashboardPanelControl from '@c/Dashboard/Components/DashboardPanelControl.vue'
import FilterLinesComponent from '@c/Dashboard/Components/FilterLinesComponent.vue'
import DashboardHeader from '@c/Dashboard/Components/DashboardHeader.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import BaseSimpleSearchInput from '@c/addf-package/components/BaseInput/BaseSimpleSearchInput.vue'
import WalkmanButton from '@c/WalkmanButton.vue'
import DisplayOptionsTaskContent from '@c/Dashboard/Content/Task/panel/DisplayOptionsTaskContent.vue'
import TaskDossierModal from '@ui/modal/taskDossier/TaskDossierModal.vue'
import TaskActiveSettingsChips from './components/TaskActiveSettingsChips.vue'
import TaskQuickCategoryFilters from './components/TaskQuickCategoryFilters.vue'
import TaskDashboardSkeleton from './components/TaskDashboardSkeleton.vue'
import TaskBulkActionBar from './components/TaskBulkActionBar.vue'
import ConfirmModal from '@c/addf-package/components/modal/ConfirmModal.vue'
import TaskCardsView from './views/TaskCardsView.vue'
import TaskBoardView from './views/TaskBoardView.vue'
import TaskListView from './views/TaskListView.vue'
import {
  groupCollapseKey,
  normalizeTaskDashboardSettings,
  setPrimarySort,
  withoutElapsedFuneralDays
} from './utils/taskDashboardSettings'
import { columnLabel } from './utils/taskDashboardColumns'
import { TaskSelection } from './utils/taskSelection'
import { TASK_ACTIVITY_LOG_EVENTS } from '@u/constants'

/**
 * Resolved lazily: the view components import the task card, whose import
 * graph may reach the dashboard container and therefore this file; reading
 * them at call time avoids a possible circular import.
 */
const resolveViewComponent = (view) =>
  markRaw(
    {
      cards: TaskCardsView,
      board: TaskBoardView,
      list: TaskListView
    }[view]
  )
const TASKS_PER_GROUP = 30
const GROUPS_LIMIT = 20
const SEARCH_DEBOUNCE_MS = 300
const DONE_STATUS = 2
/** Same bound as BulkDeleteTaskDossierRequest::MAX_IDS on the API. */
const MAX_BULK_DELETE = 500

export default {
  name: 'TaskContent',
  components: {
    TaskDossierModal,
    DisplayOptionsTaskContent,
    TaskActiveSettingsChips,
    TaskQuickCategoryFilters,
    TaskDashboardSkeleton,
    TaskBulkActionBar,
    ConfirmModal,
    BaseButton,
    BaseSimpleSearchInput,
    WalkmanButton,
    DashboardPanelControl,
    FilterLinesComponent,
    DashboardHeader
  },
  props: {
    currentBoard: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      // Deeply reactive in data(): its count reads unwrapped, like any data property.
      selection: new TaskSelection(),
      maxBulkDelete: MAX_BULK_DELETE,
      isBulkDeleting: false,
      localDashboard: null,
      loading: false,
      isFetchingTasks: false,
      groups: [],
      meta: null,
      search: '',
      searchTimer: null,
      selectedCategoryId: null,
      fetchTasksRequestId: 0,
      lastSentRequestJson: null,
      requireSaveDashboard: false,
      showEditTaskModal: false,
      modalEditionMode: false
    }
  },
  computed: {
    ...mapGetters({
      task_categories: 'metadata/getMetadataTaskCategories',
      task_statuses: 'metadata/getMetadataTaskStatuses',
      availableFilter: 'metadata/getAvailableFilterTaskDossier',
      columns: 'metadata/getTaskDossierColumns'
    }),
    settings() {
      return this.localDashboard.sort
    },
    /** The global journal preset on the task events, as the address book does. */
    taskActivityLogRoute() {
      return {
        name: 'activity_log_route',
        query: { events: TASK_ACTIVITY_LOG_EVENTS.join(',') }
      }
    },
    view: {
      get() {
        return this.settings.view
      },
      set(view) {
        this.updateSettings({ ...this.settings, view })
      }
    },
    viewOptions() {
      return [
        {
          id: 'cards',
          label: this.$t('global.task_view_cards'),
          icon: ['fal', 'grid-2']
        },
        {
          id: 'board',
          label: this.$t('global.task_view_board'),
          icon: ['fal', 'square-kanban']
        },
        {
          id: 'list',
          label: this.$t('global.task_view_list'),
          icon: ['fal', 'list']
        }
      ]
    },
    viewComponent() {
      return resolveViewComponent(this.settings.view)
    },
    validFilter() {
      return (
        this.localDashboard.filters?.filter(
          (filter) =>
            filter.column &&
            filter.values != null &&
            (Array.isArray(filter.values) ? filter.values.length > 0 : true) &&
            filter.condition
        ) ?? []
      )
    },
    /**
     * On the board, the visible status columns restrict the request so the server only
     * returns (and pages through) tasks of those columns: an existing single status
     * line is intersected with them, otherwise a status line is appended.
     */
    requestFilters() {
      const visibleStatuses = this.settings.visibleStatuses ?? []
      if (this.settings.view !== 'board' || visibleStatuses.length === 0) {
        return this.validFilter
      }
      const statusLines = this.validFilter.filter(
        (filter) => filter.column === 'status'
      )
      const [statusLine] = statusLines
      if (
        statusLines.length !== 1 ||
        (statusLine.whereType ?? 'where') !== 'where'
      ) {
        return [
          ...this.validFilter,
          {
            column: 'status',
            condition: 'in',
            values: [...visibleStatuses],
            whereType: 'where'
          }
        ]
      }
      const filteredStatuses = (
        Array.isArray(statusLine.values)
          ? statusLine.values
          : [statusLine.values]
      ).map(Number)
      const isExclusion = ['not_in', '!='].includes(statusLine.condition)
      const restrictedStatuses = visibleStatuses.filter(
        (statusId) => filteredStatuses.includes(statusId) !== isExclusion
      )

      return this.validFilter.map((filter) =>
        filter === statusLine
          ? { ...filter, condition: 'in', values: restrictedStatuses }
          : filter
      )
    },
    requestBody() {
      // category_id travels apart from `filters`: the API narrows groups and tasks with it
      // but keeps counting every category, so the other quick-filter chips stay clickable.
      return {
        filters: this.requestFilters,
        category_id: this.selectedCategoryId,
        search: this.search.trim() || null,
        group: this.settings.group.column,
        group_direction: this.settings.group.direction,
        sort: this.settings.sort,
        per_group: TASKS_PER_GROUP,
        groups_limit: GROUPS_LIMIT
      }
    },
    /** Status columns the board draws: the visible ones, else every status. */
    boardColumnCount() {
      return (
        this.settings.visibleStatuses?.length || this.task_statuses?.length || 4
      )
    },
    statusCounts() {
      return this.meta?.status_counts ?? {}
    },
    hasNoTasks() {
      return (
        this.meta !== null && this.groups.every((group) => group.count === 0)
      )
    },
    categoryCounts() {
      return this.meta?.category_counts ?? {}
    },
    categoryTotals() {
      return this.meta?.category_totals ?? {}
    },
    statusClickCycle() {
      return this.settings.statusInteraction === 'cycle'
    },
    hidesDoneTasks() {
      return this.validFilter.some((filter) => {
        if (filter.column !== 'status') {
          return false
        }
        const includesDone = (
          Array.isArray(filter.values) ? filter.values : [filter.values]
        )
          .map(Number)
          .includes(DONE_STATUS)

        return (
          (['in', '='].includes(filter.condition) && !includesDone) ||
          (['not_in', '!='].includes(filter.condition) && includesDone)
        )
      })
    }
  },
  watch: {
    currentBoard: {
      handler(newValue, oldValue) {
        if (newValue?.id !== oldValue?.id) {
          this.fetchTaskDashboardConfig()
        }
      },
      deep: true
    },
    search() {
      clearTimeout(this.searchTimer)
      if (!this.localDashboard) {
        return
      }
      this.searchTimer = setTimeout(() => this.fetchTasks(), SEARCH_DEBOUNCE_MS)
    },
    selectedCategoryId() {
      this.fetchTasks()
    }
  },
  mounted() {
    this.fetchTaskDashboardConfig()
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer)
    this.cancelTaskRequestsAction()
  },
  methods: {
    ...mapActions({
      processFetchTemplateDashboardByIdAction:
        'dashboard/fetchTemplateDashboardById',
      processFetchAllTasksAction: 'taskDossier/fetchAllDossierTasks',
      processFetchGroupPageAction: 'taskDossier/fetchDossierTasksGroupPage',
      cancelTaskRequestsAction: 'taskDossier/cancelDossierTaskRequests',
      processUndoDeleteTaskDossierByIdAction:
        'dossier/undoDeleteTaskDossierById',
      processBulkDeleteTaskDossiersAction: 'dossier/bulkDeleteTaskDossiers'
    }),
    labelOf(columnId) {
      return columnLabel(this.columns, columnId)
    },
    getNewTask() {
      this.showEditTaskModal = true
    },
    /**
     * Kept in the dashboard settings, saved with the other display options: a group the
     * user closed stays closed after a reload. No refetch, the request does not change.
     * Stale keys are only dropped here, on a change the user saves anyway: a group missing
     * from a filtered listing is not stale, it comes back once the filter is cleared.
     */
    rememberCollapse(group) {
      const key = groupCollapseKey(group.key)
      const others = withoutElapsedFuneralDays(
        this.settings.collapsedGroups ?? [],
        this.settings.group?.column,
        moment().format('YYYY-MM-DD')
      ).filter((collapsedKey) => collapsedKey !== key)
      this.localDashboard.sort = {
        ...this.settings,
        collapsedGroups: group.collapsed ? [...others, key] : others
      }
      this.requireSaveDashboard = true
    },
    updateSettings(settings) {
      // Group keys only mean something for the column they were collapsed in.
      if (settings.group?.column !== this.settings.group?.column) {
        settings = { ...settings, collapsedGroups: [] }
      }
      this.localDashboard.sort = settings
      this.requireSaveDashboard = true
      this.fetchTasksIfRequestChanged()
    },
    /** Refetches only when the request body differs from the last one sent (group, sort, view, visible statuses, filters...). */
    fetchTasksIfRequestChanged() {
      if (JSON.stringify(this.requestBody) !== this.lastSentRequestJson) {
        this.fetchTasks()
      }
    },
    sortByColumn(column) {
      this.updateSettings(setPrimarySort(this.settings, column))
    },
    fetchTasks() {
      if (!this.localDashboard) {
        return
      }
      const requestId = ++this.fetchTasksRequestId
      const requestBody = this.requestBody
      // Groups are rebuilt: a selection made on the previous result must never be deleted.
      this.selection.clear()
      this.lastSentRequestJson = JSON.stringify(requestBody)
      this.isFetchingTasks = true
      this.processFetchAllTasksAction(requestBody)
        .then((response) => {
          // An older request (superseded, it resolves null) must not end the pending one.
          if (requestId !== this.fetchTasksRequestId) {
            return
          }
          this.isFetchingTasks = false
          // null: cancelled (board switch, unmount).
          if (response === null) {
            return
          }
          this.meta = response.meta
          const collapsedGroups = this.settings.collapsedGroups ?? []
          this.groups = response.groups.map((group) => ({
            ...group,
            page: group.body_loaded ? 1 : 0,
            loadingMore: false,
            collapsed:
              !group.body_loaded ||
              collapsedGroups.includes(groupCollapseKey(group.key))
          }))
        })
        .catch(() => {
          // apiClient already toasts HTTP errors; a failed request is retried on the next change.
          if (requestId === this.fetchTasksRequestId) {
            this.isFetchingTasks = false
            this.lastSentRequestJson = null
          }
        })
    },
    /**
     * Loads the next page of a group. Resolves to true when loaded, false when the request
     * fails (toasting only errors apiClient did not already toast), and null when the page
     * was superseded by a newer listing or cancelled: the group belongs to a stale result.
     */
    loadMore(group) {
      const requestId = this.fetchTasksRequestId
      group.loadingMore = true
      return this.processFetchGroupPageAction({
        ...this.requestBody,
        group_key: group.key,
        page: group.page + 1,
        per_page: TASKS_PER_GROUP
      })
        .then((response) => {
          if (response === null || requestId !== this.fetchTasksRequestId) {
            return null
          }
          const knownTaskIds = new Set(
            group.task_dossiers.map((task) => task.id)
          )
          group.task_dossiers.push(
            ...response.task_dossiers.filter(
              (task) => !knownTaskIds.has(task.id)
            )
          )
          group.page = response.meta.current_page
          group.has_more = response.meta.current_page < response.meta.last_page
          return true
        })
        .finally(() => {
          group.loadingMore = false
        })
        .then(
          (isLoaded) => isLoaded,
          (error) => {
            if (!error?.response) {
              this.$h.toastDanger(this.$t('toasts.something_went_wrong'))
            }
            return false
          }
        )
    },
    toggleCollapse(group) {
      group.collapsed = !group.collapsed
      this.rememberCollapse(group)
      if (!group.collapsed && !group.body_loaded && !group.loadingMore) {
        this.loadMore(group).then((isLoaded) => {
          if (isLoaded === null) {
            return
          }
          if (!isLoaded) {
            group.collapsed = true
            return
          }
          group.body_loaded = true
        })
      }
    },
    showDoneTasks() {
      const allStatusIds = (this.task_statuses ?? []).map((status) => status.id)
      this.localDashboard.filters = this.localDashboard.filters.map((filter) =>
        filter.column === 'status'
          ? { ...filter, condition: 'in', values: allStatusIds }
          : filter
      )
      this.requireSaveDashboard = true
      this.fetchTasks()
    },
    handleTaskStatusUpdated({ taskId, newStatus, task }) {
      const visibleStatuses = this.settings.visibleStatuses ?? []
      if (
        this.settings.view === 'board' &&
        visibleStatuses.length > 0 &&
        !visibleStatuses.includes(task?.status ?? newStatus)
      ) {
        this.selection.remove([taskId])
      }
      for (const group of this.groups) {
        const target = group.task_dossiers.find(
          (candidate) => candidate.id === taskId
        )
        if (!target) {
          continue
        }
        this.shiftStatusCount(target.status, task?.status ?? newStatus)
        if (task) {
          Object.assign(target, task)
        } else {
          target.status = newStatus
          target.updated_at = new Date().toISOString()
        }
      }
    },
    /**
     * Owned here rather than by the view: the refresh swaps the view for the skeleton,
     * so an undo emitted by the unmounted view would never reach this component.
     */
    handleTaskDeleted({ id: taskId }) {
      this.fetchTasks()
      this.$h.toastAction(
        'delete',
        this.$t('toasts.deleted_f', { var: this.$t('attributes.task') }),
        null,
        [
          {
            title: this.$t('button.cancel'),
            class: 'text-red-700',
            action: async () => {
              await this.processUndoDeleteTaskDossierByIdAction({
                task_id: taskId
              })
              this.fetchTasks()
            }
          }
        ]
      )
    },
    /**
     * Deletes the selection after a confirmation stating it cannot be undone: no undo in
     * the toast, unlike a single deletion. A refused deletion (a task gone or no longer
     * visible) is toasted by apiClient; both cases refetch, which clears the selection.
     */
    async handleBulkDelete() {
      // The ids confirmed by the user: a refresh while the modal is open clears the selection.
      const ids = this.selection.ids()
      if (this.isBulkDeleting || ids.length === 0) {
        return
      }
      const confirmed = await this.$refs.bulkDeleteConfirm.show({
        title: this.$t('global.tasks_bulk_delete_confirm_title', {
          count: ids.length
        }),
        message: this.$t('global.tasks_bulk_delete_confirm_message')
      })
      if (!confirmed || this.isBulkDeleting) {
        return
      }
      this.isBulkDeleting = true
      try {
        const { deleted } = await this.processBulkDeleteTaskDossiersAction({
          ids
        })
        this.$h.toastSuccess(
          this.$t('global.tasks_bulk_deleted', { count: deleted })
        )
      } catch {
        // apiClient already toasted the HTTP error.
      } finally {
        this.isBulkDeleting = false
        this.fetchTasks()
      }
    },
    shiftStatusCount(fromStatus, toStatus) {
      const statusCounts = this.meta?.status_counts
      if (!statusCounts || fromStatus === toStatus) {
        return
      }
      statusCounts[fromStatus] = Math.max(
        (statusCounts[fromStatus] ?? 0) - 1,
        0
      )
      statusCounts[toStatus] = (statusCounts[toStatus] ?? 0) + 1
    },
    fetchTaskDashboardConfig() {
      const targetId = this.currentBoard.id
      this.loading = true
      // Aborts and invalidates the pending task requests of the previous board.
      this.cancelTaskRequestsAction()
      this.fetchTasksRequestId++
      this.isFetchingTasks = false
      this.groups = []
      this.meta = null
      this.lastSentRequestJson = null
      this.selectedCategoryId = null
      this.localDashboard = null
      this.search = ''
      this.processFetchTemplateDashboardByIdAction({
        id: targetId
      }).then((response) => {
        if (this.currentBoard.id !== targetId) {
          return
        }
        this.loading = false
        this.localDashboard = response.template_dashboard
        this.localDashboard.sort = normalizeTaskDashboardSettings(
          this.localDashboard.sort
        )
        this.fetchTasks()
      })
    },
    closePopover(close) {
      if (typeof close === 'function') {
        close()
      }
      this.fetchTasksIfRequestChanged()
    },
    dashboardChanged() {
      this.fetchTaskDashboardConfig()
    }
  }
}
</script>
